/**
 * VerifyEmailPage - Email Verification Page
 *
 * Handles email verification when user clicks the link in the verification email.
 * Calls the backend API to verify the token and displays the result.
 */

import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

type VerificationStatus = 'loading' | 'success' | 'error' | 'expired' | 'resending' | 'resent';

export function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [resendMessage, setResendMessage] = useState<string>('');
  const verificationAttempted = useRef(false);

  useEffect(() => {
    // Prevent double-calling in React strict mode
    if (verificationAttempted.current) return;
    verificationAttempted.current = true;

    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage(t('auth.verifyEmail.invalidToken'));
        return;
      }

      try {
        const response = await api.get(`/auth/verify-email/${token}`);
        if (response.data.success) {
          setStatus('success');
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/auth/login', { 
              state: { message: t('auth.verifyEmail.successRedirect') }
            });
          }, 3000);
        } else {
          if (response.data.error?.code === 'TOKEN_EXPIRED') {
            setStatus('expired');
          } else {
            setStatus('error');
          }
          setErrorMessage(response.data.error?.message || t('auth.verifyEmail.error'));
        }
      } catch (error: unknown) {
        const axiosError = error as { response?: { data?: { error?: { code?: string; message?: string } } } };
        if (axiosError.response?.data?.error?.code === 'TOKEN_EXPIRED') {
          setStatus('expired');
          setErrorMessage(t('auth.verifyEmail.expired'));
        } else {
          setStatus('error');
          setErrorMessage(axiosError.response?.data?.error?.message || t('auth.verifyEmail.error'));
        }
      }
    };

    verifyEmail();
  }, [token, navigate, t]);

  const handleResendVerification = async () => {
    if (!email.trim()) {
      setResendMessage(t('auth.verifyEmail.enterEmail'));
      return;
    }
    
    setStatus('resending');
    setResendMessage('');
    
    try {
      const response = await api.post('/auth/request-verification', { email: email.trim().toLowerCase() });
      if (response.data.success) {
        setStatus('resent');
        setResendMessage(t('auth.verifyEmail.resendSuccess'));
      } else {
        setStatus('expired');
        setResendMessage(response.data.error?.message || t('auth.verifyEmail.resendError'));
      }
    } catch {
      setStatus('expired');
      setResendMessage(t('auth.verifyEmail.resendError'));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 text-center">
          {/* Logo */}
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto bg-gradient-to-br from-violet-500 to-violet-600 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>

          {/* Loading State */}
          {status === 'loading' && (
            <>
              <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {t('auth.verifyEmail.verifying')}
              </h2>
              <p className="text-slate-500 dark:text-slate-400">
                {t('auth.verifyEmail.pleaseWait')}
              </p>
            </>
          )}

          {/* Success State */}
          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {t('auth.verifyEmail.success')}
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                {t('auth.verifyEmail.successMessage')}
              </p>
              <Link
                to="/auth/login"
                className="inline-flex items-center justify-center px-6 py-3 bg-violet-600 text-white font-medium rounded-lg hover:bg-violet-700 transition-colors"
              >
                {t('auth.verifyEmail.goToLogin')}
              </Link>
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {t('auth.verifyEmail.errorTitle')}
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                {errorMessage}
              </p>
              <Link
                to="/auth/login"
                className="inline-flex items-center justify-center px-6 py-3 bg-slate-600 text-white font-medium rounded-lg hover:bg-slate-700 transition-colors"
              >
                {t('auth.verifyEmail.backToLogin')}
              </Link>
            </>
          )}

          {/* Expired State */}
          {(status === 'expired' || status === 'resending') && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 dark:bg-amber-900/40 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {t('auth.verifyEmail.expiredTitle')}
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                {t('auth.verifyEmail.expiredMessage')}
              </p>
              
              {/* Resend verification form */}
              <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-3">
                  {t('auth.verifyEmail.resendDescription')}
                </p>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.verifyEmail.emailPlaceholder')}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500 mb-3"
                  disabled={status === 'resending'}
                />
                <button
                  onClick={handleResendVerification}
                  disabled={status === 'resending'}
                  className="w-full px-4 py-2 bg-violet-600 text-white font-medium rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'resending' ? t('auth.verifyEmail.resending') : t('auth.verifyEmail.resendButton')}
                </button>
                {resendMessage && (
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                    {resendMessage}
                  </p>
                )}
              </div>
              
              <div className="mt-4">
                <Link
                  to="/auth/login"
                  className="text-violet-600 dark:text-violet-400 hover:underline text-sm"
                >
                  {t('auth.verifyEmail.backToLogin')}
                </Link>
              </div>
            </>
          )}

          {/* Resent State */}
          {status === 'resent' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                {t('auth.verifyEmail.resentTitle')}
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                {t('auth.verifyEmail.resentMessage')}
              </p>
              <Link
                to="/auth/login"
                className="inline-flex items-center justify-center px-6 py-3 bg-violet-600 text-white font-medium rounded-lg hover:bg-violet-700 transition-colors"
              >
                {t('auth.verifyEmail.backToLogin')}
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default VerifyEmailPage;
